/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  faBold,
  faItalic,
  faLinkHorizontal,
  faUnderline,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSelectedNode } from '../../url';
import { FloatingLinkEditor } from '../LinkPlugin';
import {
  StyledFormatButton,
  StyledFormatGroup,
  StyledToolbarPlugin,
} from './ToolbarPlugin.styled';

const LOW_PRIORITY = 1;

export const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const [isLink, setIsLink] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));

      // Update link format
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _) => {
          updateToolbar();
          return false;
        },
        LOW_PRIORITY,
      ),
    );
  }, [editor, updateToolbar]);

  const toggleBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const toggleItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const toggleUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  };

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  return (
    <StyledToolbarPlugin ref={toolbarRef}>
      <StyledFormatGroup>
        <StyledFormatButton $active={isBold} onClick={toggleBold}>
          <FontAwesomeIcon icon={faBold} />
        </StyledFormatButton>
        <StyledFormatButton $active={isItalic} onClick={toggleItalic}>
          <FontAwesomeIcon icon={faItalic} />
        </StyledFormatButton>
        <StyledFormatButton $active={isUnderline} onClick={toggleUnderline}>
          <FontAwesomeIcon icon={faUnderline} />
        </StyledFormatButton>
        <StyledFormatButton $active={isLink} onClick={insertLink}>
          <FontAwesomeIcon icon={faLinkHorizontal} />
        </StyledFormatButton>
        {isLink &&
          createPortal(
            // @ts-ignore
            <FloatingLinkEditor editor={editor} />,
            document.body,
          )}
      </StyledFormatGroup>
    </StyledToolbarPlugin>
  );
};
